import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Card,
  CardContent, 
  styled,
  TablePagination,
  Tooltip,
  Avatar,
  Popover
} from '@mui/material';
import { 
  ManageSearch as ManageSearchIcon,
  Edit as EditIcon, 
  Search as SearchIcon,
  AccountBalance as BalanceIcon
} from '@mui/icons-material';
import { ContextSession } from "../../services/ContextSession";

// Styled components (se mantienen igual)
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  '&.MuiTableCell-head': {
    backgroundColor: '#19438C',
    color: 'white',
    fontSize: 14,
    fontWeight: 'bold',
    padding: '16px 12px',
  },
  '&.MuiTableCell-body': {
    fontSize: 13,
    borderRight: 'none',
    borderLeft: 'none',
    padding: '12px 12px',
  },
}));

const Header = styled(Paper)(({ theme }) => ({
  background: '#19438C',
  color: 'white',
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderRadius: '12px',
}));

const StatCard = styled(Card)(({ theme }) => ({
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  borderRadius: '8px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  },
}));

const SecretariaLegal = () => {
  const navigate = useNavigate();
  
  // Estados principales
  const [tramites, setTramites] = useState([]);
  const [loadingTramites, setLoadingTramites] = useState(false);

  // Estados para búsqueda y filtros
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchName, setSearchName] = useState('');
  const [filterType, setFilterType] = useState('');
  const [searchID, setSearchID] = useState('');
  const [searchExpediente, setSearchExpediente] = useState('');
  const [searchDemandado, setSearchDemandado] = useState('');

  // Estados para paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Estado para mensajes
  const [message, setMessage] = useState({ type: '', text: '' });
    
    let sessi = useContext(ContextSession);

  // URLs de las APIs
  const API_LISTA = 'https://bei360.injupemp.gob.hn:8444/api/tramite/listar/';

  // Obtener lista de trámites
  const fetchTramites = async () => {
    setLoadingTramites(true);
    try {
      const response = await fetch(API_LISTA);
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      const data = await response.json();
      setTramites(data);
    } catch (error) {
      console.error('Error al obtener trámites:', error);
      setMessage({
        type: 'error',
        text: 'Error al cargar la lista de trámites'
      });
    } finally {
      setLoadingTramites(false);
    }
  };

  // Cargar trámites al montar el componente
  useEffect(() => {
    fetchTramites();
  }, []);

  // Función de filtrado optimizada
  const filteredTramites = useMemo(() => {
    return tramites.filter(tramite => {
      const matchesName = searchName ? 
        tramite.nombredemandante?.toLowerCase().includes(searchName.toLowerCase()) : true;
      
      const matchesID = searchID ? 
        tramite.tramiteid?.toString().includes(searchID) : true;
      
      const matchesExpediente = searchExpediente ? 
        tramite.numeroexpediente?.toLowerCase().includes(searchExpediente.toLowerCase()) : true;

      const matchesDemandado = searchDemandado ? 
        tramite.nombredemandado?.toLowerCase().includes(searchDemandado.toLowerCase()) : true;

      return matchesName && matchesID && matchesExpediente && matchesDemandado;
    });
  }, [tramites, searchName, searchID, searchExpediente, searchDemandado]);

  // Trámites paginados
  const paginatedTramites = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredTramites.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredTramites, page, rowsPerPage]);

  // Manejar cambio de página
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Manejar cambio de filas por página
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calcular estadísticas basadas en trámites filtrados
  const stats = useMemo(() => {
    const totalTramites = filteredTramites.length;
    const tramitesConMonto = filteredTramites.filter(t => t.montodemandado).length;
    const tramitesConProvision = filteredTramites.filter(t => t.montoprovision).length;
    const tramitesAFavor = filteredTramites.filter(t => t.naturaleza === 1).length;
    const tramitesEnContra = filteredTramites.filter(t => t.naturaleza === 2).length;

    return {
      totalTramites,
      tramitesConMonto,
      tramitesConProvision,
      tramitesAFavor,
      tramitesEnContra
    };
  }, [filteredTramites]);

  // Handlers para búsqueda
  const handleFilterClick = useCallback((event, type) => {
    setAnchorEl(event.currentTarget);
    setFilterType(type);
  }, []);

  const handleFilterClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSearch = useCallback((setter) => (event) => {
    setter(event.target.value);
    setPage(0);
  }, []);

  const clearAllFilters = useCallback(() => {
    setSearchName('');
    setSearchID('');
    setSearchExpediente('');
    setSearchDemandado('');
    setPage(0);
  }, []);

  // Navegar a la página de edición
   const handleEditTramite = useCallback((demanda) => {
     sessi.setVariable("demandaID", demanda.tramiteid);
     sessi.setVariable("demandaData", demanda);
     navigate(`/Legal/EditarSecretaria`);
   }, [navigate, sessi]);

  // Funciones de utilidad
  const formatNumber = (value) => {
    if (value == null || value === '') return 'No asignado';
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No asignada';
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const getNaturalezaColor = (naturaleza) => {
    switch (naturaleza) {
      case 1: return 'success';
      case 2: return 'error';
      default: return 'default';
    }
  };

  const getNaturalezaText = (naturaleza) => {
    switch (naturaleza) {
      case 1: return 'A Favor';
      case 2: return 'En Contra';
      default: return 'No definida';
    }
  };

  const getEstadoColor = (estado) => {
  switch(estado) {
    case 'En proceso': return 'warning';
    case 'Creada': return 'info';
    case 'Cerrado': return 'success';
    default: return 'default';
  }
};

  // Verificar si hay filtros activos
  const hasActiveFilters = searchName || searchID || searchExpediente || searchDemandado;

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <Header>
        <Grid container alignItems="center" spacing={3}>
          <Grid item>
            <Avatar
              sx={{
                width: 60,
                height: 60,
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <BalanceIcon sx={{ fontSize: 32 }} />
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Gestión de Montos Legales
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              Administración y control de montos demandados y provisiones
            </Typography>
          </Grid>
        </Grid>
      </Header>

      {/* Tarjetas de Estadísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="overline">
                {hasActiveFilters ? 
                  `Filtrado: ${filteredTramites.length} de ${tramites.length}` : 
                  'Todas las demandas'}
              </Typography>
               <Typography variant="h4" color="primary" fontWeight="bold">
                {stats.totalTramites}
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
       
        <Grid item xs={12} sm={6} md={4}>
          <StatCard>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="overline">
               Demandas A Favor
              </Typography>
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {stats.tramitesAFavor}
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="overline">
                Demandas En Contra
              </Typography>
              <Typography variant="h4" color="error.main" fontWeight="bold">
                {stats.tramitesEnContra}
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
      </Grid>

      {message.text && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      {/* Lista de Trámites con Paginación */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell align="center">
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <strong>ID</strong>
                    <Tooltip title="Buscar por ID" arrow>
                      <IconButton onClick={(e) => handleFilterClick(e, 'searchID')} size="small">
                        <ManageSearchIcon style={{ color: 'white' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </StyledTableCell>
                
                <StyledTableCell>N° DEMANDA</StyledTableCell>
                
                <StyledTableCell align="center">
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <strong>N° EXPEDIENTE</strong>
                    <Tooltip title="Buscar por Expediente" arrow>
                      <IconButton onClick={(e) => handleFilterClick(e, 'searchExpediente')} size="small">
                        <ManageSearchIcon style={{ color: 'white' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </StyledTableCell>
                
                <StyledTableCell>NATURALEZA</StyledTableCell>
                
                <StyledTableCell align="center">
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <strong>DEMANDADO</strong>
                    <Tooltip title="Buscar Demandado" arrow>
                      <IconButton onClick={(e) => handleFilterClick(e, 'searchDemandado')} size="small">
                        <ManageSearchIcon style={{ color: 'white' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </StyledTableCell>
                
                <StyledTableCell align="center">
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <strong>DEMANDANTE</strong>
                    <Tooltip title="Buscar Demandante" arrow>
                      <IconButton onClick={(e) => handleFilterClick(e, 'search')} size="small">
                        <ManageSearchIcon style={{ color: 'white' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </StyledTableCell>
                
                <StyledTableCell>ESTADO</StyledTableCell>
                <StyledTableCell align="right">MONTO DEMANDADO</StyledTableCell>
                <StyledTableCell align="right">MONTO PROVISIÓN</StyledTableCell>
                <StyledTableCell>FECHA CREACIÓN</StyledTableCell>
                <StyledTableCell align="center">ACCIONES</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingTramites ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }} color="textSecondary">
                      Cargando trámites...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedTramites.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                    <Typography variant="h6" color="textSecondary">
                      {hasActiveFilters ? 
                        'No se encontraron trámites con los filtros aplicados' : 
                        'No se encontraron trámites'}
                    </Typography>
                    {hasActiveFilters && (
                      <Button 
                        variant="outlined" 
                        onClick={clearAllFilters}
                        sx={{ mt: 2 }}
                      >
                        Limpiar filtros
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedTramites.map((tramite) => (
                  <StyledTableRow key={tramite.tramiteid} hover>
                    <StyledTableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        #{tramite.tramiteid}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {tramite.numerodemanda}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {tramite.numeroexpediente}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Chip 
                        label={getNaturalezaText(tramite.naturaleza)}
                        color={getNaturalezaColor(tramite.naturaleza)}
                        size="small"
                        variant="outlined"
                      />
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="body2">
                        {tramite.nombredemandado || 'No asignado'}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="body2">
                        {tramite.nombredemandante || 'No asignado'}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Chip 
                        label={tramite.estado} 
                        color={getEstadoColor(tramite.estado)}
                        size="small"
                        variant="outlined"
                      />
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <Typography 
                        color={tramite.montodemandado ? 'success.main' : 'text.secondary'}
                        fontWeight={tramite.montodemandado ? 'bold' : 'normal'}
                        variant="body2"
                      >
                        {formatNumber(tramite.montodemandado)}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <Typography 
                        color={tramite.montoprovision ? 'info.main' : 'text.secondary'}
                        fontWeight={tramite.montoprovision ? 'bold' : 'normal'}
                        variant="body2"
                      >
                        {formatNumber(tramite.montoprovision)}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2">
                        {formatDate(tramite.fechacreacion)}
                      </Typography>
                    </StyledTableCell>
                    {tramite.estado?.toLowerCase() !== 'cerrado' && (
                    <StyledTableCell align="center">
                      <IconButton
                        color="primary"
                        onClick={() => handleEditTramite(tramite)}
                        title="Editar montos"
                        size="small"
                        sx={{
                          bgcolor: 'primary.light',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'primary.main',
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </StyledTableCell>
                    )}
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Popover de búsqueda */}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleFilterClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Box p={2} sx={{ width: 300 }}>
            {filterType === 'search' && (
              <TextField
                placeholder="Buscar por Demandante"
                variant="outlined"
                value={searchName}
                onChange={handleSearch(setSearchName)}
                InputProps={{ 
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> 
                }}
                fullWidth
                autoFocus
              />
            )}
            {filterType === 'searchID' && (
              <TextField
                placeholder="Buscar por ID"
                variant="outlined"
                value={searchID}
                onChange={handleSearch(setSearchID)}
                InputProps={{ 
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> 
                }}
                fullWidth
                autoFocus
              />
            )}
            {filterType === 'searchExpediente' && (
              <TextField
                placeholder="Buscar por N° Expediente"
                variant="outlined"
                value={searchExpediente}
                onChange={handleSearch(setSearchExpediente)}
                InputProps={{ 
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> 
                }}
                fullWidth
                autoFocus
              />
            )}
            {filterType === 'searchDemandado' && (
              <TextField
                placeholder="Buscar por Demandado"
                variant="outlined"
                value={searchDemandado}
                onChange={handleSearch(setSearchDemandado)}
                InputProps={{ 
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> 
                }}
                fullWidth
                autoFocus
              />
            )}
          </Box>
        </Popover>
        
        {/* Paginación */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredTramites.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        />
      </Paper>
    </Container>
  );
};

export default SecretariaLegal;